import { useState, useEffect, useContext } from 'react'
import { useMutation, useLazyQuery } from "@apollo/client";
import { useDisclosure, useToast } from "@chakra-ui/react";
import {
    PUBLISHERCATALOG,
    TIMES_REDEEMED_COUPON
} from "../graphql/queries";
import {
    CREATE_STRIPE_COUPON,
    UPDATE_STRIPE_COUPON,
    DELETE_STRIPE_COUPON,
    CREATE_CONNECTED_ACCOUNT,
    CREATE_CONNECTED_ACCOUNT_LINK
} from "../graphql/mutations";
import { refresh_url, return_url } from "../config";
import { PublisherAndUserContext } from '../contexts/PublisherAndUserContext';

export const useStripeCoupon = () => {
    const { user, publisher } = useContext(PublisherAndUserContext);
    const [coupon, setCoupon] = useState({
        pricingPlanType: "Subscription",
        couponCode: "",
        percentageOff: null,
        appliesToProduct: [],
        maxRedemption: null,
        couponExpiryDate: "",
        duration: "once",
        durationInMonths: null
    });
    const [coupons, setCoupons] = useState([])
    const [catalogs, setCatalogs] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const {
        isOpen: addCouponIsOpen,
        onOpen: addCouponOnOpen,
        onClose: addCouponOnClose
    } = useDisclosure();
    const {
        isOpen: statusCouponIsOpen,
        onOpen: statusCouponOnOpen,
        onClose: statusCouponOnClose
    } = useDisclosure();
    const toast = useToast();

    const [createStripeCoupon] = useMutation(CREATE_STRIPE_COUPON, {
        onCompleted: (data) => {
            if (data.edshopifyCreateCoupon.isSuccess) {
                addCouponOnClose()
                setLoading(false);
                const couponCopy = data.edshopifyCreateCoupon
                delete couponCopy.isSuccess
                delete couponCopy.isDuplicate
                setCoupons([...coupons, {
                    ...couponCopy,
                    couponExpiryDate: new Date(data.edshopifyCreateCoupon.couponExpiryDate).toISOString().split('T')[0],
                    expiryDateUTCFormat: data.edshopifyCreateCoupon.couponExpiryDate,
                    timesRedeemed: 0
                }])
                toast({
                    position: "top-right",
                    description: `New Coupon Created!`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
            } else if (!data.edshopifyCreateCoupon.isSuccess && data.edshopifyCreateCoupon.isDuplicate) {
                setLoading(false);
                toast({
                    position: "top-right",
                    description: `${coupon.couponCode} already exists. Please provide a new code.`,
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                });
            }
        },
        error: () => {
            setLoading(false);
            toast({
                position: "top-right",
                description: `Error while creating instructor. Please try again.`,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        },
    });
    const [updateStripeCoupon] = useMutation(UPDATE_STRIPE_COUPON, {
        onCompleted: (data) => {
            statusCouponOnClose()
            let couponsCopy = [...coupons]
            let index = couponsCopy.findIndex(c => c.id === data.edshopifyUpdateStripeCoupon.id)
            couponsCopy[index] = { ...couponsCopy[index], isActive: data.edshopifyUpdateStripeCoupon.isActive }
            setCoupons([...couponsCopy])
            setLoading(false);
            toast({
                position: "top-right",
                description: `Updated status!`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        },
        error: () => {
            setLoading(false);
            toast({
                position: "top-right",
                description: `Error. Please try again.`,
                status: "error",
                duration: 2000,
                isClosable: true,
            });
        },
    });
    const [deleteCoupon] = useMutation(DELETE_STRIPE_COUPON, {
        onCompleted: (data) => {
            setLoading(false);
            let couponCopy = [...coupons]
            couponCopy = couponCopy.filter(c => (c.id !== data.edshopifyDeleteStripeCoupon.id))
            setCoupons(couponCopy)
            toast({
                position: "top-right",
                description: `Deleted!`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        },
        error: () => {
            setLoading(false);
            toast({
                position: "top-right",
                description: `Error deleting coupon. Please try again.`,
                status: "error",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const [getCatalogs] = useLazyQuery(PUBLISHERCATALOG, {
        onCompleted: (data) => {
            setCatalogs(
                data.publisher.catalogs.map((catalog, cindex) => ({
                    ...catalog,
                    index:
                        typeof catalog.index === "object"
                            ? cindex + 1
                            : catalog.index === cindex + 2
                                ? cindex + 1
                                : catalog.index,
                }))
            );
            setLoading(false);
        },
        onError: () => {
            setLoading(false);
        },
    });

    const [timesRedeemedCoupon] = useLazyQuery(TIMES_REDEEMED_COUPON, {
        onCompleted: (data) => {
            setLoading(false);
            let couponsCopy = [...coupons]
            couponsCopy = couponsCopy.map((c, index) => {
                const match = data.edshopifyTimesRedeemedCoupon.coupons.find(etr => etr.id === c.id);
                if (match) {
                    return { ...c, timesRedeemed: match.times_redeemed ? match.times_redeemed : 0 };
                }
                return c;
            });
            setCoupons([...couponsCopy])
        },
        onError: () => {
            setLoading(false);
        },
    });

    const createCoupon = async (e) => {
        e.preventDefault();
        if (/[^a-zA-Z0-9]/.test(coupon.couponCode)) {
            return
        }
        setLoading(true);
        createStripeCoupon({
            variables: {
                where: {
                    id: publisher?.id
                },
                data: {
                    pricingPlanType: coupon.pricingPlanType,
                    couponCode: coupon.couponCode,
                    gatewayProvider: "stripe",
                    percentageOff: Number(coupon.percentageOff),
                    appliesToProduct: [...selectedItems],
                    maxRedemption: Number(coupon.maxRedemption),
                    couponExpiryDate: (new Date(coupon.couponExpiryDate)),
                    ...(coupon.pricingPlanType === "Subscription" ? {
                        duration: coupon.duration,
                        durationInMonths: Number(coupon.durationInMonths)
                    } : {})
                }
            },
        });
    };

    const updateCoupon = async (e, status) => {
        e.preventDefault();
        setLoading(true);
        updateStripeCoupon({
            variables: {
                data: {
                    isActive: status,
                },
                where: {
                    id: publisher?.id,
                    promoCodeId: coupon.otherInfo?.promotionCodeId,
                    couponId: coupon.id
                }
            }
        })
    }

    const deleteStripeCoupon = (data) => {
        setLoading(true);
        deleteCoupon({
            variables: {
                where: {
                    id: publisher?.id,
                    couponId: data.id
                }
            }
        })
    }

    const [createConnectAccount] = useMutation(CREATE_CONNECTED_ACCOUNT, {
        onCompleted: (data) => {
            createConnectAccountLink({
                variables: {
                    data: {
                        refresh_url: refresh_url,
                        return_url: return_url
                    }
                }
            })
        },
        onError: () => {
            setLoading(false)
        },
    });

    const [createConnectAccountLink] = useMutation(CREATE_CONNECTED_ACCOUNT_LINK, {
        onCompleted: (data) => {
            setLoading(false)
            window.location.href = data.edshopifyCreateSConnectedAccountLink.url
        },
        onError: () => {
            setLoading(false)
        },
    });

    const createStripeConnectedAccount = () => {
        setLoading(true)
        createConnectAccount({
            variables: {
                data: {
                    email: user?.email,
                    country: "US"
                },
                where: {
                    id: publisher?.id
                }
            }
        })
    }

    const createStripeConnectedAccountLink = () => {
        setLoading(true)
        createConnectAccountLink({
            variables: {
                data: {
                    refresh_url: refresh_url,
                    return_url: return_url
                },
                where: {
                    id: publisher?.id
                }
            }
        })
      }

    const onChange = (type, e) => {
        if (type === "pricingPlanType") {
            setCoupon({
                ...coupon,
                [type]: e
            })
        } else {
            setCoupon({
                ...coupon,
                [type]: type === "duration" ? e : e.target.value
            })
        }
    };

    const selectProduct = (selectedList) => {
        setSelectedItems(selectedList);
    };

    const removeProduct = (selectedList, removedItem) => {
        const newList = selectedList.filter(list => list.id !== removedItem.id)
        setSelectedItems(newList);
    }

    const statusCouponCancel = () => {
        setCoupon({
            pricingPlanType: "Subscription",
            couponCode: "",
            percentageOff: null,
            appliesToProduct: [],
            maxRedemption: null,
            couponExpiryDate: "",
            duration: "once",
            durationInMonths: null
        })
        statusCouponOnClose()
    }

    const onEdit = (data) => {
        setIsUpdate(true)
        setCoupon({
            ...data,
            duration: data.otherInfo.duration,
            durationInMonths: data.otherInfo.durationInMonths
        })
        addCouponOnOpen()
    }

    useEffect(() => {
        getCatalogs({
            variables: { where: { id: publisher?.id } },
        });
        if (publisher.Coupon.length > 0) {
            setCoupons(publisher.Coupon.map(c => ({
                ...c,
                status: c.isActive ? "Active" : "Inactive",
                couponExpiryDate: new Date(c.couponExpiryDate).toISOString().split('T')[0],
                expiryDateUTCFormat: c.couponExpiryDate,
                timesRedeemed: 0
            })))
            timesRedeemedCoupon({
                variables: {
                    where: {
                        id: publisher?.id,
                        couponIds: ["cm732r7e10000pbtkg5i0rvpr", "cm7341j810000pbp5vmd2prk5", "cm7334a6s0002pbtkc0lgwefp"] //publisher?.Coupon?.map(c => c.id)
                    }
                }
            })
        }

    }, [publisher.id, publisher.Coupon, getCatalogs]);

    return {
        coupon,
        addCouponIsOpen,
        addCouponOnOpen,
        addCouponOnClose,
        onChange,
        loading,
        publisher,
        catalogs,
        createCoupon,
        coupons,
        deleteStripeCoupon,
        statusCouponIsOpen,
        statusCouponOnOpen,
        setCoupon,
        selectProduct,
        removeProduct,
        selectedItems,
        statusCouponCancel,
        onEdit,
        isUpdate,
        updateCoupon,
        user,
        createStripeConnectedAccount,
        createStripeConnectedAccountLink
    }
}
